import React, { useState, useRef } from "react";
import {
  TooltipContainer,
  TooltipTextContainer,
  IconContainer,
  ToolTipWrapper,
  TooltipBodyWrapper,
  LightTooltipBeak,
} from "./Tooltip.styles";
import { Body6RegularTypography } from "../../primitives/Typography/typographyVariants";
import { colorPalette } from "../../theme/colors";
import { useClickOutside } from "../../hooks/useClickOutside";
import { TooltipProps } from "./Tooltip.types";
import { IMAGE_PREFIX, transformMedia } from "../../utils/constants";

export const Tooltip: React.FC<TooltipProps> = ({
  component,
  variant = "left",
  appearance = "dark",
  children,
  onClick,
  triggerOnHover = false,
}) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const isLightAppearance = appearance === "light";
  const showImageBeak =
    !isLightAppearance && (variant === "left" || variant === "top");
  const showBottomImageBeak =
    !isLightAppearance && (variant === "right" || variant === "bottom");
  const showLightBeak =
    isLightAppearance && (variant === "top" || variant === "bottom");

  const handleMouseEnter = () => {
    if (triggerOnHover) {
      setVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (triggerOnHover) {
      setVisible(false);
    }
  };

  const setTooltipVisible = (isVisible: boolean) => {
    if (!triggerOnHover) {
      setVisible(isVisible);
    }
    if (onClick && isVisible) {
      onClick();
    }
  };

  const handleTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (triggerOnHover) {
      return;
    }

    setVisible((currentVisible) => {
      const willOpen = !currentVisible;
      if (willOpen && onClick) {
        onClick();
      }
      return willOpen;
    });
  };

  useClickOutside(tooltipRef, () => setTooltipVisible(false));

  const tooltipContent =
    typeof component === "string" ? (
      <Body6RegularTypography
        color={
          isLightAppearance
            ? colorPalette.popBlack[500]
            : colorPalette.popWhite[500]
        }
      >
        {component}
      </Body6RegularTypography>
    ) : (
      component
    );

  return (
    <ToolTipWrapper ref={tooltipRef}>
      <div
        onClick={handleTriggerClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
      {visible && (
        <TooltipContainer variant={variant} $appearance={appearance}>
          {showImageBeak && (
            <IconContainer
              src={transformMedia(
                variant === "top"
                  ? `${IMAGE_PREFIX}Tooltip.png`
                  : `${IMAGE_PREFIX}TooltipRight.png`
              )}
              variant={variant}
            />
          )}
          <TooltipBodyWrapper>
            {showLightBeak && <LightTooltipBeak />}
            <TooltipTextContainer $appearance={appearance}>
              {tooltipContent}
            </TooltipTextContainer>
          </TooltipBodyWrapper>
          {showBottomImageBeak && (
            <IconContainer
              src={transformMedia(
                variant === "bottom"
                  ? `${IMAGE_PREFIX}Tooltip.png`
                  : `${IMAGE_PREFIX}TooltipRight.png`
              )}
              variant={variant}
            />
          )}
        </TooltipContainer>
      )}
    </ToolTipWrapper>
  );
};
